import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ArrowDown, ArrowUp, ChevronsDownUp, ChevronsUpDown, CornerDownRight, Diamond,
  ListIndentDecrease, ListIndentIncrease, Plus, Trash2,
} from 'lucide-react'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuPortal, DropdownMenuSeparator,
  DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useGanttStore } from '../store/ganttStore'
import { subtreeIds, type Tree } from '../lib/tree'
import type { LocalLink } from '../lib/predecessors'
import type { ScheduleActivity } from '../types'
import type { ForeignPredecessor } from './GanttRow'
import type { RowOps } from './row-ops'

export interface RowContextMenuState { activityId: string; x: number; y: number }

interface Props {
  state: RowContextMenuState | null
  onClose: () => void
  ops: RowOps
  canEdit: boolean
  tree: Tree<ScheduleActivity>
  byId: Map<string, ScheduleActivity>
  collapsedIds: Set<string>
  predecessorsByActivity: Map<string, { local: LocalLink[]; foreign: ForeignPredecessor[] }>
  successorsByActivity: Map<string, { local: string[]; foreign: ForeignPredecessor[] }>
  onSetCollapsed: (ids: string[], collapsed: boolean) => void
  onNavigateExternal: (ref: { id: string; scheduleId: string }) => void
}

/**
 * Right-click menu on a grid row (spec B §9.5, Task 16). Opened by `GanttView.onRowContextMenu` at
 * the pointer position via a zero-size, `position: fixed` trigger — Radix positions the content
 * relative to that point. Structural ops mirror `GanttEditingGroup`; "Go to predecessor/successor"
 * and "Collapse/Expand subtree" are context-menu-only and available even when `!canEdit` (they
 * navigate/toggle, they don't mutate).
 */
const RowContextMenu: React.FC<Props> = ({
  state, onClose, ops, canEdit, tree, byId, collapsedIds, predecessorsByActivity, successorsByActivity, onSetCollapsed, onNavigateExternal,
}) => {
  const { t } = useTranslation()
  const pendingCount = useGanttStore((s) => s.pendingCount)
  const structuralDisabled = pendingCount > 0

  const activityId = state ? state.activityId : null
  const node = activityId ? tree.byId.get(activityId) ?? null : null
  const hasChildren = !!node && node.children.length > 0
  const collapsed = !!activityId && collapsedIds.has(activityId)

  const preds = activityId ? predecessorsByActivity.get(activityId) : undefined
  const succs = activityId ? successorsByActivity.get(activityId) : undefined
  const predLocal = useMemo(
    () => (preds ? preds.local : []).map((l) => byId.get(l.predecessorId)).filter((a): a is ScheduleActivity => !!a),
    [preds, byId],
  )
  const succLocal = useMemo(
    () => (succs ? succs.local : []).map((id) => byId.get(id)).filter((a): a is ScheduleActivity => !!a),
    [succs, byId],
  )
  const predForeign = preds ? preds.foreign : []
  const succForeign = succs ? succs.foreign : []

  if (!state) return null

  return (
    <DropdownMenu open onOpenChange={(open) => { if (!open) onClose() }}>
      <DropdownMenuTrigger asChild>
        <span
          className="fixed pointer-events-none"
          // ui-drift: dynamic
          style={{ left: state.x, top: state.y }}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" onCloseAutoFocus={(e) => e.preventDefault()}>
        {canEdit && (
          <>
            {/* Review fix (Important #4, spec §10): Add activity/milestone/sub-activity need
                `canCreate`, not just `canEdit` — hidden (not disabled) for a member with EDIT but
                not CREATE, same as the toolbar's `GanttEditingGroup`. */}
            {ops.canAdd && (
              <>
                <DropdownMenuItem disabled={structuralDisabled} onClick={() => ops.addActivity('task')}>
                  <Plus className="h-4 w-4" />{t('Schedule_op_add')}
                </DropdownMenuItem>
                <DropdownMenuItem disabled={structuralDisabled} onClick={() => ops.addActivity('milestone')}>
                  <Diamond className="h-4 w-4" />{t('Schedule_op_add_milestone')}
                </DropdownMenuItem>
                <DropdownMenuItem disabled={structuralDisabled} onClick={() => ops.addActivity('child')}>
                  <CornerDownRight className="h-4 w-4" />{t('Schedule_op_add_child')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem disabled={structuralDisabled || !ops.canIndent} onClick={ops.indent}>
              <ListIndentIncrease className="h-4 w-4" />{t('Schedule_op_indent')}
            </DropdownMenuItem>
            <DropdownMenuItem disabled={structuralDisabled || !ops.canOutdent} onClick={ops.outdent}>
              <ListIndentDecrease className="h-4 w-4" />{t('Schedule_op_outdent')}
            </DropdownMenuItem>
            <DropdownMenuItem disabled={structuralDisabled || !ops.canMoveUp} onClick={ops.moveUp}>
              <ArrowUp className="h-4 w-4" />{t('Schedule_op_up')}
            </DropdownMenuItem>
            <DropdownMenuItem disabled={structuralDisabled || !ops.canMoveDown} onClick={ops.moveDown}>
              <ArrowDown className="h-4 w-4" />{t('Schedule_op_down')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={structuralDisabled || !ops.canRemove}
              className="text-destructive focus:text-destructive"
              onClick={ops.remove}
            >
              <Trash2 className="h-4 w-4" />{t('Schedule_op_delete')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger disabled={!predLocal.length && !predForeign.length}>
            {t('Schedule_op_goto_pred')}
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>
              {predLocal.map((a) => (
                <DropdownMenuItem key={a.id} onClick={() => ops.goTo(a.id)}>{a.wbsCode} {a.name}</DropdownMenuItem>
              ))}
              {predForeign.map((f) => (
                <DropdownMenuItem key={`${f.scheduleId}-${f.id}`} onClick={() => onNavigateExternal(f)}>
                  ⧉ {f.scheduleName} {f.wbsCode} {f.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger disabled={!succLocal.length && !succForeign.length}>
            {t('Schedule_op_goto_succ')}
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>
              {succLocal.map((a) => (
                <DropdownMenuItem key={a.id} onClick={() => ops.goTo(a.id)}>{a.wbsCode} {a.name}</DropdownMenuItem>
              ))}
              {succForeign.map((f) => (
                <DropdownMenuItem key={`${f.scheduleId}-${f.id}`} onClick={() => onNavigateExternal(f)}>
                  ⧉ {f.scheduleName} {f.wbsCode} {f.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
        {hasChildren && activityId && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled={collapsed} onClick={() => onSetCollapsed([activityId], true)}>
              <ChevronsDownUp className="h-4 w-4" />{t('Schedule_op_collapse')}
            </DropdownMenuItem>
            <DropdownMenuItem disabled={!collapsed} onClick={() => onSetCollapsed(subtreeIds(tree, activityId), false)}>
              <ChevronsUpDown className="h-4 w-4" />{t('Schedule_op_expand')}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default RowContextMenu
